import {Component, computed, inject, Signal} from '@angular/core';
import {FormsModule} from '@angular/forms';

import {MenuItem} from 'primeng/api';

import {LoaderComponent, MapEntryPipe, SplitButtonComponent, SvgSpriteIcon} from '@selpia/shared/ui';
import {PerspectiveFacade} from '@selpia/perspective/data-access';
import {AppConfigFacade, DimensionHelper} from '@selpia/app-config/data-access';
import {NavigationService} from '@selpia/data-batch/history-access';
import {Dimension, DimensionLevel} from '@selpia/shared/models';
import {MonitoringService} from '@selpia/user/tracking';

@Component({
  selector: 'lib-feature-dimension-selector',
  imports: [LoaderComponent, FormsModule, SplitButtonComponent, MapEntryPipe],
  templateUrl: './feature-dimension-selector.component.html',
  styleUrl: './feature-dimension-selector.component.scss',
  host: {
    'data-test': 'dimension-selector',
  },
})
export class FeatureDimensionSelectorComponent {
  readonly #perspectiveFacade = inject(PerspectiveFacade);
  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #navigationService = inject(NavigationService);
  readonly #monitoringService = inject(MonitoringService);

  protected readonly dimensions: Signal<(Dimension & {disabled?: boolean})[]> = computed(
    () => this.#appConfigFacade.dimensions() || [],
  );

  protected readonly focusDimension: Signal<Dimension | undefined> = computed(() =>
    DimensionHelper.getDimensionOf(this.#perspectiveFacade.focusDimensionLevel(), this.dimensions()),
  );

  protected readonly isLoading: Signal<boolean> = this.#appConfigFacade.isLoading;

  protected readonly isDataReady: Signal<boolean> = computed(() => !this.isLoading() && !!this.#perspectiveFacade.perspective());

  protected readonly dimensionLevelsByDimension = computed(() => {
    const filterDimensions = this.#perspectiveFacade.perspective()?.filterDimensions || [];
    const dimensions = this.#appConfigFacade.dimensions() || [];

    if (!dimensions.length) {
      return new Map<string, MenuItem[]>();
    }

    return new Map<string, MenuItem[]>(dimensions.map(
      dimension => [dimension.name, dimension.levels.filter(
        (level: DimensionLevel) => level.levelNumber !== 0)
        .map(
          (dimensionLevel: DimensionLevel) => {
            const disabled = filterDimensions.some(
              filter => filter.dimension.name === dimension.name && (filter.dimensionLevel?.levelNumber || 0) >= dimensionLevel.levelNumber
            );

            return {
              label: dimensionLevel.name,
              command: () => this.setDimensionLevel(dimensionLevel),
              disabled,
            };
          },
        )]) || []);
  });

  setDimensionLevel(focusDimensionLevel: DimensionLevel) {
    this.#navigationService.updatePerspective({focusDimensionLevel});
    this.#monitoringService.logEvent('DimensionSelected', {dimensionName: focusDimensionLevel.name, dimensionLevel: focusDimensionLevel.levelNumber, view: 'Tilegrid'});
  }

  updateDimension(dimension: Dimension | undefined) {
    if (dimension) {
      this.#navigationService.updatePerspective({
        focusDimensionLevel: DimensionHelper.getMatchingLevel(dimension),
        filterDimensions: [],
      });
    }
  }

  protected readonly SvgSpriteIcon = SvgSpriteIcon;
}
